import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { StrategyValidateResult, StrategyYamlView } from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";

/**
 * Edits the phase's own YAML block. The server splices the result back into the
 * strategy document, so comments and every other phase survive untouched, and
 * validates the whole document before persisting.
 */
export default function PhaseYamlEditor({
  strategySlug,
  phaseSlug,
  onSaved,
}: {
  strategySlug: string;
  phaseSlug: string;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const path = `/api/v1/strategies/${strategySlug}/phases/${phaseSlug}/yaml`;

  const blockQ = useQuery({
    queryKey: ["phase-yaml", strategySlug, phaseSlug],
    queryFn: () => api.get<StrategyYamlView>(path),
  });

  const [draft, setDraft] = useState("");
  const [result, setResult] = useState<StrategyValidateResult | null>(null);

  // Reset the editor whenever a different phase is opened or the saved block
  // changes underneath us, so a draft never leaks across phases.
  useEffect(() => {
    setDraft(blockQ.data?.yaml ?? "");
    setResult(null);
  }, [blockQ.data?.yaml, phaseSlug]);

  const save = useMutation({
    mutationFn: (yaml: string) => api.put<StrategyValidateResult>(path, { yaml }),
    onSuccess: (r) => {
      setResult(r);
      if (!r.valid) return;
      queryClient.invalidateQueries({ queryKey: ["phase-yaml", strategySlug] });
      onSaved();
    },
  });

  const saved = blockQ.data?.yaml ?? "";
  const dirty = draft !== saved;

  return (
    <Card>
      <CardTitle>Phase YAML</CardTitle>
      <p className="text-xs text-[var(--fg-muted)] mb-2">
        Everything above, as text. Useful for pasting a phase in from elsewhere or for a
        shape the form does not draw. Saving rewrites only this block; the phase's{" "}
        <code>id</code> cannot be changed here.
      </p>

      {blockQ.isLoading ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : blockQ.error ? (
        <p className="text-sm text-[var(--danger)]">Could not load this phase's YAML.</p>
      ) : (
        <>
          <textarea
            aria-label="Phase YAML"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setResult(null);
            }}
            rows={16}
            spellCheck={false}
            className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-card)] p-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate(draft)}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save phase"}
            </button>
            <button
              type="button"
              disabled={!dirty || save.isPending}
              onClick={() => {
                setDraft(saved);
                setResult(null);
              }}
              className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50 disabled:opacity-50"
            >
              Revert
            </button>
            {result?.valid ? (
              <span role="status" className="text-sm text-[var(--success)]">
                Saved.
              </span>
            ) : null}
            {result && !result.valid ? (
              <span role="alert" className="text-sm text-[var(--danger)]">
                {result.error}
              </span>
            ) : null}
            {save.error ? (
              <span role="alert" className="text-sm text-[var(--danger)]">
                {save.error instanceof ApiError
                  ? `Save failed (${save.error.status})`
                  : "Save failed."}
              </span>
            ) : null}
          </div>
        </>
      )}
    </Card>
  );
}
